import {DataGrid, GridPaginationModel} from "@mui/x-data-grid";
import React, {Dispatch, SetStateAction, useEffect, useState, useRef} from "react";
import LocaleText from "./LocaleText";
import {Request} from "../../Requests/request";
import {IFilter} from "../PageFilters/types";
import useGlobalContext from "../../Hooks/useGlobalContext";

interface IGenerateColumns {
    (params: {
        setLoading: Dispatch<SetStateAction<boolean>>
    }): any[]
}

interface IHandlingRequestCall {
    (request: Request, params: any): Promise<any>
}

interface IResourceTableProps {
    request: Request;
    generateColumns: IGenerateColumns;
    pageSizeOptions?: number[];
    getRowId?: (params: any) => number;
    handlingRequestCall?: IHandlingRequestCall;
    minHeight?: string | number;
    rechargeRequest?: number;
    autoPageSize?: boolean;
    filterDefinitions: (params: any) => any; //trocar any para um tipo mutável
}

const resetPaginate = (pageSize: number = 10): GridPaginationModel => ({
    page: 0,
    pageSize: pageSize,
});

const ResourceTable: React.FC<IResourceTableProps> = ({
                                                          request,
                                                          generateColumns,
                                                          pageSizeOptions = [10, 25, 50, 100],
                                                          getRowId = (row) => row?.id,
                                                          handlingRequestCall = (request, params) => request.get(params),
                                                          minHeight = 200,
                                                          rechargeRequest = 0,
                                                          autoPageSize = false,
                                                          filterDefinitions
                                                      }) => {
    const { filters } = useGlobalContext();
    const [list, setList] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(() => 
        resetPaginate(pageSizeOptions?.[0] ?? 10)
    );
    const isInitialMount = useRef(true);

    // Efeito para resetar a página QUANDO OS FILTROS MUDAM.
    // Ignora a montagem inicial para evitar uma chamada dupla.
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }
    }, [filters, rechargeRequest]);

    // Efeito para buscar dados QUANDO A PAGINAÇÃO MUDA (ou na carga inicial via 'filters').
    useEffect(() => {
        let active = true;
        const load = async () => {
            setLoading(true);
            const params = filterDefinitions({
                ...filters,
                page: paginationModel.page + 1,
                recordsPerPage: paginationModel.pageSize,
            });
            try {
                const value = await handlingRequestCall(request, params);
                if (active) setList(value);
            } catch (error) {
                console.error("Failed to fetch data:", error);
                if (active) setList({ data: [], total: 0 });
            } finally {
                if (active) setLoading(false);
            }
        };

        load();

        return () => {
            active = false;
        };
    }, [paginationModel, filters, rechargeRequest]); // A dependência de 'filters' aqui é segura por causa do reset de página no outro useEffect

    return (
        <DataGrid
            localeText={LocaleText}
            rows={list?.data || []}
            columns={generateColumns({ setLoading })}
            paginationMode="server"
            rowCount={list?.total || list?.meta?.total || 0}
            onPaginationModelChange={setPaginationModel}
            paginationModel={paginationModel}
            loading={loading}
            pageSizeOptions={pageSizeOptions}
            getRowId={getRowId}
            sx={{ minHeight: minHeight }}
            autoPageSize={autoPageSize}
        />
    );
}

export type {IGenerateColumns}
export default ResourceTable;